import { useCallback, useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { Box, FormHelperText, Grid, List, ListItem, Typography } from "@mui/material"
import { BillReceiverType, ConfirmedDomain, CreateUpdateUserGroupFormValues } from "../userGroupsTypes"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { CreateBillDialog } from "../../../accounting/bill-receivers/components/CreateBillDialog"
import { BillReceiversItem } from "../../../accounting/bill-receivers/billReceiversTypes"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { ActionMenu, AddButton, SelectionDialog } from "./Elements"
import { CreateUpdateDomainForm } from "./CreateUpdateDomainForm"
import { AddBillReceiver } from "./AddBillReceiver"
import { deleteDomain } from "../userGroupsApi"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"

const useStyles = makeStyles()((theme) => ({
    listTitle: {
        color: theme.palette.tailwind.slate[900],
    },
    listEmpty: {
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
        padding: "1.25rem 0.75rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    listItemContainer: {
        display: "grid",
        alignItems: "center",
        gap: "0.5rem",
        paddingLeft: "1.5rem",
        paddingRight: "1rem",
    },
    listItemContainerThreeColumns: {
        gridTemplateColumns: "62% 28% 10%",
    },
    listItemContainerFourColumns: {
        gridTemplateColumns: "26% 36% 30% 8%",
    },
    listHead: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    listHeadText: {
        fontSize: "0.75rem",
        fontWeight: 600,
        color: "black",
    },
    listItem: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
    },
    listItemText: {
        color: "black",
    },
    listItemTextGrey: {
        color: theme.palette.tailwind.slate[700],
        wordWrap: "break-word",
    },
    infoWrap: {
        display: "grid",
        gridTemplateColumns: "1.5rem 1fr",
        background: "#E5F6FD",
        borderRadius: "0.25rem",
        gap: "0.75rem",
        padding: "1rem",
        color: theme.palette.tailwind.sky[700],
        marginBottom: "2rem",
    },
    infoText: {
        color: "#014361",
    },
}))

const defaultConfirmDialogState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}

export const DomainsList = () => {
    const { values, setFieldValue, errors } = useFormikContext<CreateUpdateUserGroupFormValues>()
    const { l10n } = useAppLocalization()
    const { classes, cx } = useStyles()
    const [dialogState, setDialogState] = useState<boolean>(false)
    const [domainId, setDomainId] = useState<number | null>(null)
    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultConfirmDialogState)

    const handleSaveDomain = (data?: ConfirmedDomain) => {
        if (data) {
            setFieldValue("domains", [...values.domains, data])
        }
        setDialogState(false)
    }

    const { mutate: callDeleteDomain } = useApiMutation(deleteDomain, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params) {
            const updatedDomains = [...values.domains].filter((domain) => domain.id !== params.domainId)
            setFieldValue("domains", updatedDomains)
        },
    })

    const handleDelete = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteDomain({ domainId: confirmModalState.itemId })
        } else {
            throw new Error("User group id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteDomain])

    return (
        <Grid container gap={3} flexDirection="column">
            <LocalizedStrict id="create-or-edit-company-user-group-connected-domains">
                <Typography variant="subtitle2" className={classes.listTitle}>
                    Connected domains
                </Typography>
            </LocalizedStrict>
            <Grid>
                {values.domains && values.domains.length > 0 ? (
                    <List>
                        <ListItem
                            className={cx(
                                classes.listItemContainer,
                                classes.listItemContainerThreeColumns,
                                classes.listHead,
                            )}
                        >
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-domains-table-domain">
                                <Typography variant="body2" className={classes.listHeadText}>
                                    Domain
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-domains-table-status">
                                <Typography variant="body2" textAlign="center" className={classes.listHeadText}>
                                    Status
                                </Typography>
                            </LocalizedStrict>
                        </ListItem>
                        {values.domains.map((item) => (
                            <ListItem
                                key={`item_${item.id}`}
                                className={cx(
                                    classes.listItemContainer,
                                    classes.listItemContainerThreeColumns,
                                    classes.listItem,
                                )}
                            >
                                <Typography variant="body2" className={classes.listItemText}>
                                    {item.domain}
                                </Typography>
                                <Grid container alignItems="center" justifyContent="center" fontWeight={500}>
                                    {item.isConfirmed ? (
                                        <LocalizedStrict id="create-or-edit-company-user-group-connected-domains-table-status-active">
                                            <StandardChip variant="success" placement="table">Active</StandardChip>
                                        </LocalizedStrict>
                                    ) : (
                                        <LocalizedStrict id="create-or-edit-company-user-group-connected-domains-table-status-pending">
                                            <StandardChip variant="yellow" placement="table">Pending / Invitation sent</StandardChip>
                                        </LocalizedStrict>
                                    )}
                                </Grid>
                                {item.isConfirmed ? (
                                    <ActionMenu
                                        item={item}
                                        menuItems={[
                                            {
                                                text: l10n.getString(
                                                    "create-or-edit-company-user-group-connected-domains-table-delete",
                                                ),
                                                onClick: () =>
                                                    setConfirmModalState({
                                                        showConfirmModal: true,
                                                        confirmModalText: `${item.domain}`,
                                                        itemId: item.id,
                                                    }),
                                            },
                                        ]}
                                    />
                                ) : (
                                    <ActionMenu
                                        item={item}
                                        menuItems={[
                                            {
                                                text: l10n.getString(
                                                    "create-or-edit-company-user-group-connected-domains-table-resend-invitation",
                                                ),
                                                onClick: () => {
                                                    setDialogState(true)
                                                    setDomainId(item.id)
                                                },
                                            },
                                            {
                                                text: l10n.getString(
                                                    "create-or-edit-company-user-group-connected-domains-table-delete",
                                                ),
                                                onClick: () =>
                                                    setConfirmModalState({
                                                        showConfirmModal: true,
                                                        confirmModalText: `${item.domain}`,
                                                        itemId: item.id,
                                                    }),
                                            },
                                        ]}
                                    />
                                )}
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <LocalizedStrict id="create-or-edit-company-user-group-connected-domains-empty">
                        <Typography variant="body2" className={classes.listEmpty}>
                            You have not created any domains yet.
                        </Typography>
                    </LocalizedStrict>
                )}
                {errors?.domains && (
                    <FormHelperText error>
                        {Array.isArray(errors.domains) ? errors.domains.join(", ") : errors.domains}
                    </FormHelperText>
                )}
            </Grid>
            <LocalizedStrict
                id="create-or-edit-company-user-group-connected-domains-add-button"
                attrs={{ title: true }}
            >
                <AddButton title="Add domain" setDialogState={setDialogState} />
            </LocalizedStrict>
            {dialogState && (
                <SelectionDialog
                    dialogState={dialogState}
                    setDialogState={setDialogState}
                    title={
                        domainId
                            ? l10n.getString(
                                  "create-or-edit-company-user-group-connected-domains-modal-resend-invitation",
                              )
                            : l10n.getString("create-or-edit-company-user-group-connected-domains-modal-add-domain")
                    }
                >
                    <Box className={classes.infoWrap}>
                        <FontAwesomeIcon size="lg" icon={faCircleInfo} />
                        <Grid container flexDirection="column">
                            <Typography variant="subtitle1" className={classes.infoText}>
                                Title
                            </Typography>
                            <Typography variant="body2" className={classes.infoText}>
                                Description
                            </Typography>
                        </Grid>
                    </Box>

                    <CreateUpdateDomainForm domainId={domainId} onSaveDomain={handleSaveDomain} />
                </SelectionDialog>
            )}
            {confirmModalState.showConfirmModal && (
                <LocalizedStrict
                    id="company-organization-user-groups-table-delete-modal-domain"
                    attrs={{ typeOfEntity: true }}
                >
                    <DeleteConfirmDialog
                        typeOfEntity="domain"
                        entityName={confirmModalState.confirmModalText}
                        handleClick={handleDelete}
                        open={confirmModalState.showConfirmModal}
                        onClose={() => setConfirmModalState(defaultConfirmDialogState)}
                    />
                </LocalizedStrict>
            )}
        </Grid>
    )
}

export const BillReceiversList = () => {
    const { values, setFieldValue, errors } = useFormikContext<CreateUpdateUserGroupFormValues>()
    const { l10n } = useAppLocalization()
    const { classes, cx } = useStyles()
    const { companyId } = useCurrentCompany()

    const [dialogState, setDialogState] = useState<boolean>(false)
    const [isOpenCreateBill, setIsOpenCreateBill] = useState(false)

    const handleDelete = (id: number) => {
        const items = [...values.billReceivers]
        const filteredItems = items.filter((item) => item.id !== id)
        setFieldValue("billReceivers", filteredItems)
    }

    const handleChooseBillReceiver = (receiver?: BillReceiversItem) => {
        if (receiver) {
            const { id, name, addressDisplayText, endpoint } = receiver
            const items = [...values.billReceivers]
            const filteredItems = items.filter((item) => item.id !== id)

            setFieldValue("billReceivers", [
                ...filteredItems,
                { id, name, endpoint, displayAddress: addressDisplayText },
            ])
        }
        setDialogState(false)
    }

    const handleCreateNewBill = () => {
        setDialogState(false)
        setIsOpenCreateBill(true)
    }

    return (
        <Grid container gap={3} flexDirection="column">
            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver">
                <Typography variant="subtitle2" className={classes.listTitle}>
                    Bill receiver
                </Typography>
            </LocalizedStrict>
            <Grid>
                {values.billReceivers && values.billReceivers.length > 0 ? (
                    <List>
                        <ListItem
                            className={cx(
                                classes.listItemContainer,
                                classes.listItemContainerFourColumns,
                                classes.listHead,
                            )}
                        >
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-table-name">
                                <Typography variant="body2" className={classes.listHeadText}>
                                    Name
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-table-address">
                                <Typography variant="body2" className={classes.listHeadText}>
                                    Address
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-table-end-point">
                                <Typography variant="body2" textAlign="center" className={classes.listHeadText}>
                                    End point
                                </Typography>
                            </LocalizedStrict>
                        </ListItem>
                        {values.billReceivers.map((item) => (
                            <ListItem
                                key={`item_${item.id}_${item.name}`}
                                className={cx(
                                    classes.listItemContainer,
                                    classes.listItemContainerFourColumns,
                                    classes.listItem,
                                )}
                            >
                                <Typography variant="body2" className={classes.listItemText}>
                                    {item.name}
                                </Typography>
                                <Typography variant="body2" className={classes.listItemText}>
                                    {item.displayAddress}
                                </Typography>
                                <Typography variant="body2" textAlign="center" className={classes.listItemTextGrey}>
                                    {item.endpoint.type === BillReceiverType.EAN_NUMBER
                                        ? item.endpoint.eanNumber
                                        : item.endpoint.email}
                                </Typography>
                                <ActionMenu
                                    item={item}
                                    menuItems={[
                                        {
                                            text: l10n.getString(
                                                "create-or-edit-company-user-group-connected-bill-receiver-table-remove",
                                            ),
                                            onClick: () => handleDelete(item.id),
                                        },
                                    ]}
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-empty">
                        <Typography variant="body2" className={classes.listEmpty}>
                            "You have not created bill receiver yet.
                        </Typography>
                    </LocalizedStrict>
                )}
                {errors?.billReceivers && (
                    <FormHelperText error>
                        {Array.isArray(errors.billReceivers) ? errors.billReceivers.join(", ") : errors.billReceivers}
                    </FormHelperText>
                )}
            </Grid>
            <LocalizedStrict
                id="create-or-edit-company-user-group-connected-bill-receiver-add-button"
                attrs={{ title: true }}
            >
                <AddButton title="Add bill receiver" setDialogState={setDialogState} />
            </LocalizedStrict>
            {dialogState && (
                <AddBillReceiver
                    type="new"
                    dialogState={dialogState}
                    companyId={companyId}
                    setDialogState={setDialogState}
                    handleChooseBillReceiver={handleChooseBillReceiver}
                    handleCreateNewBill={handleCreateNewBill}
                />
            )}
            {isOpenCreateBill && (
                <CreateBillDialog
                    onSelectBillReceiver={() => {}}
                    open={isOpenCreateBill}
                    companyId={companyId}
                    onClose={() => setIsOpenCreateBill(false)}
                    title={l10n.getString("bill-receivers-create-dialog-title")}
                />
            )}
        </Grid>
    )
}
